import { API_BASE_URL } from '../../apiConfig';
import eventStreamService from '../supabaseClient/eventStreamService';
import { supabase } from '../supabaseClient/supabaseClient';

const MAX_STRING_LENGTH = 4000;
const MAX_ARRAY_LENGTH = 20;
const MAX_OBJECT_KEYS = 40;

let installed = false;
let authSubscription = null;
let cachedUserId = null;

function normalizeText(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function truncateString(value) {
  const text = String(value || '');
  if (text.length <= MAX_STRING_LENGTH) return text;
  return `${text.slice(0, MAX_STRING_LENGTH)}...[truncated]`;
}

function sanitizeValue(value, depth = 0) {
  if (depth > 4) return '[max_depth]';
  if (value == null) return value;
  if (typeof value === 'string') return truncateString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_LENGTH).map((item) => sanitizeValue(item, depth + 1));
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, MAX_OBJECT_KEYS)
        .map(([key, item]) => [key, sanitizeValue(item, depth + 1)])
    );
  }
  return String(value);
}

function getByPath(source, path) {
  if (!source || typeof source !== 'object') return null;
  const parts = String(path || '')
    .split('.')
    .filter(Boolean);
  let current = source;
  for (const part of parts) {
    if (!current || typeof current !== 'object' || !(part in current)) return null;
    current = current[part];
  }
  return current;
}

function firstNonEmptyText(...values) {
  for (const value of values) {
    const text = normalizeText(value);
    if (text) return text;
  }
  return null;
}

function extractEventIds(requestPayload, responsePayload) {
  const req = requestPayload && typeof requestPayload === 'object' ? requestPayload : {};
  const res = responsePayload && typeof responsePayload === 'object' ? responsePayload : {};

  const courseId = firstNonEmptyText(
    req.courseId,
    req.course_id,
    req.courseDetails?.id,
    req.course?.id,
    getByPath(req, 'currentTopic.courseId'),
    res.courseId,
    res.course_id,
    res.course?.id
  );

  const unitId = firstNonEmptyText(
    req.unitId,
    req.unit_id,
    req.lessonId,
    req.lesson_id,
    getByPath(req, 'currentTopic.id'),
    res.unitId,
    res.unit_id,
    res.lessonId,
    res.lesson_id
  );

  const activityId = firstNonEmptyText(
    req.activityId,
    req.activity_id,
    req.taskId,
    getByPath(req, 'focusedTask.id'),
    res.activityId,
    res.activity_id
  );

  const lessonId = firstNonEmptyText(
    req.lessonId,
    req.lesson_id,
    req.lessonTitle,
    res.lessonId,
    res.lesson_id,
    res.title
  );

  const examId = firstNonEmptyText(req.examId, req.exam_id, res.examId, res.exam_id);
  const reflectionId = firstNonEmptyText(
    req.reflectionId,
    req.reflection_id,
    req.date,
    res.reflectionId,
    res.reflection_id,
    res.id
  );
  const taskId = firstNonEmptyText(
    req.taskId,
    req.task_id,
    getByPath(req, 'focusedTask.id'),
    res.taskId,
    res.task_id
  );
  const sceneId = firstNonEmptyText(
    req.sceneId,
    req.scene_id,
    req.topicId,
    req.topic_id,
    getByPath(req, 'currentTopic.id'),
    res.sceneId,
    res.scene_id,
    res.lessonId,
    res.lesson_id
  );

  const conversationKey = firstNonEmptyText(
    req.conversationKey,
    req.conversation_key,
    req.sessionId,
    req.session_id,
    req.activityId,
    examId
  );

  const sessionId = firstNonEmptyText(req.sessionId, req.session_id, res.sessionId, res.session_id);

  const entityType = firstNonEmptyText(
    req.entityType,
    req.entity_type,
    examId ? 'exam' : null,
    taskId ? 'task' : null,
    sceneId ? 'scene' : null,
    activityId ? 'activity' : null,
    courseId ? 'course' : null
  );

  const entityId = firstNonEmptyText(
    req.entityId,
    req.entity_id,
    examId,
    taskId,
    sceneId,
    activityId,
    unitId,
    courseId
  );

  return {
    courseId,
    unitId,
    activityId,
    lessonId,
    examId,
    reflectionId,
    taskId,
    sceneId,
    sessionId,
    conversationKey,
    entityType,
    entityId,
  };
}

function safeParseBody(body) {
  if (!body) return null;
  if (typeof body !== 'string') return sanitizeValue(body);
  try {
    return sanitizeValue(JSON.parse(body));
  } catch {
    return truncateString(body);
  }
}

function shouldTrackUrl(urlString) {
  if (!API_BASE_URL) return false;
  return typeof urlString === 'string' && urlString.startsWith(API_BASE_URL);
}

function isTrackedLlmPath(pathname) {
  const path = String(pathname || '').toLowerCase();
  return [
    '/professor',
    '/tasks/',
    '/reflection/',
    '/animated-video/',
    '/lessons/animated-',
    '/generate-lesson',
    '/generate-course',
    '/course-graph',
    '/course-creation',
    '/generate-notes',
    '/generate-resources',
    '/recommend-videos',
    '/summarize-video',
    '/generate-exam',
    '/exam/',
    '/submit-session',
    '/mostlearned/generate-exam',
    '/mostlearned/submit-session',
    '/dashboard/chat',
    '/dashboard/insights',
    '/dashboard/recommendations',
    '/dashboard/analyze-progress',
    '/courses/chat',
    '/courses/intelligent-search',
    '/courses/explain-relevance',
    '/courses/compare',
    '/courses/suggest-path',
    '/projects/chat',
    '/projects/suggestions',
    '/projects/help',
    '/projects/break-down',
    '/projects/resources',
    '/projects/generate-from-course',
    '/ai/orchestrator',
    '/chat',
  ].some((token) => path.includes(token));
}

function classifyEndpoint(pathname) {
  const path = String(pathname || '').toLowerCase();

  if (
    path.includes('/professor') ||
    path.includes('/course-creation/chat') ||
    path.includes('/projects/chat') ||
    path.includes('/dashboard/chat') ||
    path.includes('/courses/chat') ||
    path.includes('/ai/orchestrator') ||
    path.includes('/tasks/chat') ||
    path.includes('/reflection/chat') ||
    path.includes('/examiner-chat')
  ) {
    return { eventFamily: 'chat', entityType: 'llm_chat' };
  }
  if (
    path.includes('/generate-exam') ||
    path.includes('/submit-session') ||
    path.includes('/exam/')
  ) {
    return { eventFamily: 'exam', entityType: 'llm_exam' };
  }
  if (path.includes('/reflection/')) {
    return { eventFamily: 'reflection', entityType: 'llm_reflection' };
  }
  if (path.includes('/tasks/')) {
    return { eventFamily: 'task', entityType: 'llm_task' };
  }
  if (
    path.includes('/animated-video/') ||
    path.includes('/lessons/animated-') ||
    path.includes('/generate-lesson')
  ) {
    return { eventFamily: 'scene', entityType: 'llm_scene' };
  }
  if (
    path.includes('/generate-course') ||
    path.includes('/course-graph') ||
    path.includes('/course-creation')
  ) {
    return { eventFamily: 'course_creation', entityType: 'llm_course_creation' };
  }
  if (
    path.includes('/generate-notes') ||
    path.includes('/generate-resources') ||
    path.includes('/recommend-videos') ||
    path.includes('/summarize-video')
  ) {
    return { eventFamily: 'text', entityType: 'llm_text' };
  }
  return { eventFamily: 'system', entityType: 'llm_request' };
}

function fireAndForgetEvent(event) {
  Promise.resolve().then(async () => {
    try {
      let userId = cachedUserId;
      if (!userId) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        userId = session?.user?.id || null;
        cachedUserId = userId;
      }
      await eventStreamService.emitEvent({
        userId,
        ...event,
      });
    } catch {
      // Intentionally swallow event logging failures.
    }
  });
}

async function extractResponsePayload(response) {
  try {
    const clone = response.clone();
    const contentType = String(clone.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('application/json')) {
      return sanitizeValue(await clone.json());
    }
    return truncateString(await clone.text());
  } catch {
    return null;
  }
}

function installLlmEventInterceptor() {
  if (installed || typeof window === 'undefined' || typeof fetch !== 'function') return;
  installed = true;

  supabase.auth.getSession().then(({ data }) => {
    cachedUserId = data?.session?.user?.id || null;
  });
  if (!authSubscription) {
    authSubscription = supabase.auth.onAuthStateChange((_event, session) => {
      cachedUserId = session?.user?.id || null;
    });
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = undefined) => {
    const requestUrl =
      typeof input === 'string'
        ? input
        : input instanceof Request
          ? input.url
          : String(input || '');

    if (!shouldTrackUrl(requestUrl)) {
      return originalFetch(input, init);
    }

    let parsedUrl = null;
    try {
      parsedUrl = new URL(requestUrl);
    } catch {
      return originalFetch(input, init);
    }
    if (!isTrackedLlmPath(parsedUrl.pathname)) {
      return originalFetch(input, init);
    }

    const method = String(
      init?.method || (input instanceof Request ? input.method : 'GET') || 'GET'
    ).toUpperCase();
    const classification = classifyEndpoint(parsedUrl.pathname);
    const startedAt = Date.now();
    const correlationId = `llm_${startedAt}_${Math.random().toString(36).slice(2, 9)}`;
    const requestPayload = safeParseBody(init?.body || (input instanceof Request ? null : null));
    const requestIds = extractEventIds(requestPayload, null);

    fireAndForgetEvent({
      sessionId: requestIds.sessionId || correlationId,
      correlationId,
      conversationKey: requestIds.conversationKey,
      streamKey:
        requestIds.courseId ||
        requestIds.examId ||
        requestIds.sceneId ||
        requestIds.taskId ||
        requestIds.activityId
          ? [
              classification.eventFamily,
              requestIds.courseId,
              requestIds.unitId,
              requestIds.activityId,
              requestIds.examId,
              requestIds.sceneId,
              requestIds.taskId,
            ]
              .filter(Boolean)
              .join(':')
          : `${classification.eventFamily}:${parsedUrl.pathname}`,
      eventFamily: classification.eventFamily,
      eventType: 'llm_request_started',
      eventName: 'llm_request_started',
      origin: parsedUrl.pathname,
      actorType: 'user',
      actorId: null,
      entityType: requestIds.entityType || classification.entityType,
      entityId: requestIds.entityId || parsedUrl.pathname,
      courseId: requestIds.courseId,
      unitId: requestIds.unitId,
      activityId: requestIds.activityId,
      lessonId: requestIds.lessonId,
      examId: requestIds.examId,
      reflectionId: requestIds.reflectionId,
      taskId: requestIds.taskId,
      sceneId: requestIds.sceneId,
      success: true,
      payload: {
        endpoint: parsedUrl.pathname,
        method,
        request: requestPayload,
      },
    });

    try {
      const response = await originalFetch(input, init);
      const durationMs = Date.now() - startedAt;
      const responsePayload = await extractResponsePayload(response);
      const resolvedIds = extractEventIds(requestPayload, responsePayload);
      const resolvedStreamKey =
        resolvedIds.courseId ||
        resolvedIds.examId ||
        resolvedIds.sceneId ||
        resolvedIds.taskId ||
        resolvedIds.activityId
          ? [
              classification.eventFamily,
              resolvedIds.courseId,
              resolvedIds.unitId,
              resolvedIds.activityId,
              resolvedIds.examId,
              resolvedIds.sceneId,
              resolvedIds.taskId,
            ]
              .filter(Boolean)
              .join(':')
          : `${classification.eventFamily}:${parsedUrl.pathname}`;

      fireAndForgetEvent({
        sessionId: resolvedIds.sessionId || correlationId,
        correlationId,
        conversationKey: resolvedIds.conversationKey,
        streamKey: resolvedStreamKey,
        eventFamily: classification.eventFamily,
        eventType: response.ok ? 'llm_request_succeeded' : 'llm_request_failed',
        eventName: response.ok ? 'llm_request_succeeded' : 'llm_request_failed',
        origin: parsedUrl.pathname,
        actorType: 'system',
        actorId: 'backend_llm',
        entityType: resolvedIds.entityType || classification.entityType,
        entityId: resolvedIds.entityId || parsedUrl.pathname,
        courseId: resolvedIds.courseId,
        unitId: resolvedIds.unitId,
        activityId: resolvedIds.activityId,
        lessonId: resolvedIds.lessonId,
        examId: resolvedIds.examId,
        reflectionId: resolvedIds.reflectionId,
        taskId: resolvedIds.taskId,
        sceneId: resolvedIds.sceneId,
        success: response.ok,
        errorCode: response.ok ? null : `http_${response.status}`,
        durationMs,
        payload: {
          endpoint: parsedUrl.pathname,
          method,
          status: response.status,
          request: requestPayload,
          response: responsePayload,
        },
        metrics: {
          status: response.status,
          durationMs,
        },
      });

      return response;
    } catch (error) {
      const durationMs = Date.now() - startedAt;
      fireAndForgetEvent({
        sessionId: requestIds.sessionId || correlationId,
        correlationId,
        conversationKey: requestIds.conversationKey,
        streamKey:
          requestIds.courseId ||
          requestIds.examId ||
          requestIds.sceneId ||
          requestIds.taskId ||
          requestIds.activityId
            ? [
                classification.eventFamily,
                requestIds.courseId,
                requestIds.unitId,
                requestIds.activityId,
                requestIds.examId,
                requestIds.sceneId,
                requestIds.taskId,
              ]
                .filter(Boolean)
                .join(':')
            : `${classification.eventFamily}:${parsedUrl.pathname}`,
        eventFamily: classification.eventFamily,
        eventType: 'llm_request_failed',
        eventName: 'llm_request_failed',
        origin: parsedUrl.pathname,
        actorType: 'system',
        actorId: 'backend_llm',
        entityType: requestIds.entityType || classification.entityType,
        entityId: requestIds.entityId || parsedUrl.pathname,
        courseId: requestIds.courseId,
        unitId: requestIds.unitId,
        activityId: requestIds.activityId,
        lessonId: requestIds.lessonId,
        examId: requestIds.examId,
        reflectionId: requestIds.reflectionId,
        taskId: requestIds.taskId,
        sceneId: requestIds.sceneId,
        success: false,
        errorCode: error?.message || 'network_error',
        durationMs,
        payload: {
          endpoint: parsedUrl.pathname,
          method,
          request: requestPayload,
        },
        metrics: {
          durationMs,
        },
      });
      throw error;
    }
  };
}

export default installLlmEventInterceptor;
